import type { ButtonProps } from "react-aria-components";
import { Button as ReactAriaButton } from "react-aria-components";
import type { ElementType } from "react";
import type { IcIcon } from "@jetblue/kit-icons/types";
import { IcSearch } from "@jetblue/kit-icons/ic-search";

import type { ButtonVariant } from "../button/button-types";
import {
  BUTTON_STYLES,
  FOCUS_OUTLINE_STYLES,
  FOCUS_OUTLINE_STYLES_SECONDARY,
} from "../button/button-styles";
import { cn } from "../../utilities/tailwind";

export type PairedButtonProperties = {
  /**
   * Defaults to `IcSearch`. However, this component accepts an any icon as a function (from our icon library, e.g. `IcFlights`).
   */
  Icon?: IcIcon | undefined;

  /**
   * Defaults to `button`. This property allows the base element used to create the component to be changed.
   */
  as?: ElementType | undefined;

  /**
   * Defines a string value that labels the current element.
   */
  "aria-label": string;

  /**
   * Classes that are applied to the button.
   */
  className?: string | undefined;

  /**
   * The URL to link to when the button is clicked.
   */
  href?: string | undefined;

  /**
   * Classes that are applied to the icon.
   */
  iconClassName?: string | undefined;

  /**
   * These are the basic variations on the styling of a button.
   */
  variant?: ButtonVariant | undefined;

  /**
   * The target for the button.
   */
  target?: "_blank" | "_self" | undefined;
} & Omit<
  ButtonProps,
  "aria-label" | "children" | "className" | "isPending" | "style" | "slot"
>;
export const PairedButton = ({
  Icon,
  as = "button",
  className = "",
  iconClassName = "",
  variant = "primary",
  target = "_self",
  ...properties
}: PairedButtonProperties) => {
  const Component = as == "button" ? ReactAriaButton : as;
  const twClassName = cn(
    "relative flex size-14 items-center justify-center rounded-e-xl",
    BUTTON_STYLES.color[variant],
    BUTTON_STYLES.disabled[variant],
    FOCUS_OUTLINE_STYLES,
    { [FOCUS_OUTLINE_STYLES_SECONDARY]: variant === "secondary" },
    className,
  );
  return (
    <Component className={twClassName} target={target} {...properties}>
      {Icon ? (
        <Icon aria-hidden className={iconClassName} role="img" />
      ) : (
        <IcSearch aria-hidden className={iconClassName} role="img" />
      )}
    </Component>
  );
};

PairedButton.displayName = "PairedButton";
